import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import QuoteSkeleton from './QuoteSkeleton';
import ErrorState from '../ErrorState/ErrorState';
import { useCachedFetch } from './../../hooks/useCachedFetch';
import { SUBSCRIBE_API } from '../../config/api';
import { getRecaptchaToken } from '../../utils/recaptcha';
import { useConsent } from '../../hooks/useConsent';
import { triggerShowBanner } from '../CookieBanner/CookieBanner';
import { AnimatedOnScroll } from '../AnimatedOnScroll';
import './Quote.scss';

const Quote = ({ page }) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;

  const {
    data: quoteData,
    loading: dataLoading,
    error: dataError,
    refetch
  } = useCachedFetch('/data/locales/quote.json');

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isFading, setIsFading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [formKey, setFormKey] = useState(0);

  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const { hasConsentForForms } = useConsent();

  useEffect(() => {
    if (dataLoading) return;

    if (dataError) {
      setHasError(true);
      setIsLoading(false);
      return;
    }

    const data = quoteData?.QuoteData?.[page] || quoteData?.QuoteData?.MarketingPage;

    if (data && data.title && data.subtitle1 && data.subtitle2) {
      setHasError(false);
    } else {
      setHasError(true);
    }

    setIsLoading(false);
  }, [dataLoading, dataError, quoteData, page]);

  useEffect(() => {
    setEmailError("");
    setFormSubmitted(false);
    setIsFading(false);
    setFormKey(prev => prev + 1);
    setSubmitError('');
  }, [currentLang]);

  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  const pageData = useMemo(() =>
    quoteData?.QuoteData?.[page] || quoteData?.QuoteData?.MarketingPage || null,
    [quoteData, page]
  );

  const translations = useMemo(() => {
    if (!pageData) return {};

    const getTranslation = (textObj) =>
      textObj?.[currentLang] || textObj?.en || '';

    return {
      title: getTranslation(pageData.title),
      subtitle1: getTranslation(pageData.subtitle1),
      subtitle2: getTranslation(pageData.subtitle2),
    };
  }, [pageData, currentLang]);

  const errorMessages = useMemo(() => ({
    en: {
      subscribeInputText: "Enter your email address",
      emailInvalid: "Invalid email address",
      emailDomain: "Email must contain a valid domain (e.g. gmail.com)",
      emailRequired: "Please enter your email address to subscribe to updates",
      keyboardLanguage: "Switch keyboard language",
      submitting: "Sending...",
      subscribeButtonText: "Subscribe",
      subscribeSuccess: "Thank you for subscribing!",
      subscribeError: "Subscription error. Please try again.",
      emailAlreadySubscribed: "This email is already subscribed",
      rateLimitError: "Too many requests. Please try again later.",
      consentError: "Consent to personal data processing is required to subscribe",
    },
    ua: {
      subscribeInputText: "Введіть вашу електронну адресу",
      emailInvalid: "Невірна email адреса",
      emailDomain: "Email повинен містити дійсний домен (наприклад, gmail.com)",
      emailRequired: "Якщо бажаєте продовжити підписку на оновлення введіть вашу електронну адресу",
      keyboardLanguage: "Змініть мову на клавіатурі",
      submitting: "Відправка...",
      subscribeButtonText: "Підписатися",
      subscribeSuccess: "Дякуємо за підписку!",
      subscribeError: "Помилка підписки. Спробуйте ще раз.",
      emailAlreadySubscribed: "Цей email вже підписаний",
      rateLimitError: "Занадто багато запитів. Спробуйте пізніше.",
      consentError: "Для підписки необхідна згода на обробку персональних даних",
    },
    ru: {
      subscribeInputText: "Введите ваш электронный адрес",
      emailInvalid: "Неверный адрес электронной почты",
      emailDomain: "Email должен содержать действительный домен (например, gmail.com)",
      emailRequired: "Если хотите подписаться на обновления, введите ваш адрес электронной почты",
      keyboardLanguage: "Смените язык на клавиатуре",
      submitting: "Отправка...",
      subscribeButtonText: "Подписаться",
      subscribeSuccess: "Спасибо за подписку!",
      subscribeError: "Ошибка подписки. Попробуйте еще раз.",
      emailAlreadySubscribed: "Этот email уже подписан",
      rateLimitError: "Слишком много запросов. Попробуйте позже.",
      consentError: "Для подписки необходимо согласие на обработку персональных данных",
    },
    pl: {
      subscribeInputText: "Wprowadź swój adres email",
      emailInvalid: "Nieprawidłowy adres email",
      emailDomain: "Email musi zawierać prawidłową domenę (np. gmail.com)",
      emailRequired: "Proszę wprowadzić swój adres email, aby subskrybować aktualizacje",
      keyboardLanguage: "Zmień język klawiatury",
      submitting: "Wysyłanie...",
      subscribeButtonText: "Subskrybuj",
      subscribeSuccess: "Dziękujemy za subskrypcję!",
      subscribeError: "Błąd subskrypcji. Spróbuj ponownie.",
      emailAlreadySubscribed: "Ten email jest już zapisany",
      rateLimitError: "Zbyt wiele żądań. Spróbuj później.",
      consentError: "Do subskrypcji wymagana jest zgoda na przetwarzanie danych osobowych",
    },
    de: {
      subscribeInputText: "Geben Sie Ihre E-Mail-Adresse ein",
      emailInvalid: "Ungültige E-Mail-Adresse",
      emailDomain: "E-Mail muss eine gültige Domain enthalten (z.B. gmail.com)",
      emailRequired: "Bitte geben Sie Ihre E-Mail-Adresse ein, um Updates zu abonnieren",
      keyboardLanguage: "Tastatursprache wechseln",
      submitting: "Senden...",
      subscribeButtonText: "Abonnieren",
      subscribeSuccess: "Vielen Dank für Ihr Abonnement!",
      subscribeError: "Abonnementfehler. Bitte versuchen Sie es erneut.",
      emailAlreadySubscribed: "Diese E-Mail ist bereits abonniert",
      rateLimitError: "Zu viele Anfragen. Bitte versuchen Sie es später erneut.",
      consentError: "Für das Abonnement ist die Zustimmung zur Verarbeitung personenbezogener Daten erforderlich",
    },
    es: {
      subscribeInputText: "Ingrese su dirección de correo electrónico",
      emailInvalid: "Dirección de correo electrónico no válida",
      emailDomain: "El correo electrónico debe contener un dominio válido (por ejemplo, gmail.com)",
      emailRequired: "Por favor ingrese su dirección de correo electrónico para suscribirse a las actualizaciones",
      keyboardLanguage: "Cambiar idioma del teclado",
      submitting: "Enviando...",
      subscribeButtonText: "Suscribirse",
      subscribeSuccess: "¡Gracias por suscribirte!",
      subscribeError: "Error de suscripción. Inténtalo de nuevo.",
      emailAlreadySubscribed: "Este correo electrónico ya está suscrito",
      rateLimitError: "Demasiadas solicitudes. Por favor, inténtelo más tarde.",
      consentError: "Para suscribirse se requiere el consentimiento para el procesamiento de datos personales",
    },
  }), []);

  const currentErrors = useMemo(() =>
    errorMessages[currentLang] || errorMessages.en,
    [currentLang, errorMessages]
  );

  const validationSchema = useMemo(() =>
    Yup.object({
      email: Yup.string()
        .email(currentErrors.emailInvalid)
        .matches(
          /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/,
          currentErrors.emailDomain
        )
        .required(currentErrors.emailRequired),
    }),
    [currentErrors]
  );

  const handleEmailInput = useCallback((e, setFieldTouched, setFieldError) => {
    const value = e.target.value;
    if (value && /[А-Яа-яЁё]/.test(value)) {
      setEmailError(currentErrors.keyboardLanguage);
    } else {
      setEmailError("");
    }
    e.target.value = value.replace(/[^A-Za-z0-9@._-]/g, "");
    if (value) {
      setFieldTouched("email", true, false);
      setFieldError("email", "");
      setSubmitError('');
    }
  }, [currentErrors]);

  const handleSubmit = useCallback(async (values, { resetForm }) => {
    if (!hasConsentForForms()) {
      setSubmitError(currentErrors.consentError);
      setFormSubmitted(false);
      setIsSubmitting(false);
      triggerShowBanner();
      setTimeout(() => {
        setSubmitError('');
      }, 10000);
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      let recaptchaToken = '';
      try {
        recaptchaToken = await getRecaptchaToken('subscribe');
      } catch (err) {
        console.warn('reCAPTCHA error:', err);
      }

      const response = await fetch(SUBSCRIBE_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: values.email,
          language: currentLang,
          recaptchaToken: recaptchaToken,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'form_lead_submitted',
          form_name: 'newsletter_subscription'
        });

        setFormSubmitted(true);
        setSubmitError('');
        resetForm();

        setTimeout(() => {
          setIsFading(true);
          setTimeout(() => {
            setFormSubmitted(false);
            setIsFading(false);
            setIsSubmitting(false);
          }, 1000);
        }, 3000);
      } else if (response.status === 429) {
        setSubmitError(currentErrors.rateLimitError);
        setFormSubmitted(true);
        setIsSubmitting(false);
        setTimeout(() => {
          setIsFading(true);
          setTimeout(() => {
            setFormSubmitted(false);
            setIsFading(false);
            setSubmitError('');
          }, 1000);
        }, 3000);
      } else {
        let userMessage = '';

        if (data.message && (
          data.message.includes('already subscribed') ||
          data.message.includes('вже підписаний') ||
          data.message.includes('уже подписан') ||
          data.message.includes('już zapisany') ||
          data.message.includes('bereits abonniert') ||
          data.message.includes('ya está suscrito')
        )) {
          userMessage = currentErrors.emailAlreadySubscribed;
        } else {
          userMessage = data.errors?.[0]?.msg || data.message || currentErrors.subscribeError;
        }

        setSubmitError(userMessage);
        setFormSubmitted(true);
        setIsSubmitting(false);

        setTimeout(() => {
          setIsFading(true);
          setTimeout(() => {
            setFormSubmitted(false);
            setIsFading(false);
            setSubmitError('');
          }, 1000);
        }, 3000);
      }
    } catch (error) {
      console.error('Network error:', error);
      setSubmitError(currentErrors.subscribeError);
      setFormSubmitted(true);
      setIsSubmitting(false);

      setTimeout(() => {
        setIsFading(true);
        setTimeout(() => {
          setFormSubmitted(false);
          setIsFading(false);
          setSubmitError('');
        }, 1000);
      }, 3000);
    }
  }, [currentLang, currentErrors, hasConsentForForms]);

  const hasValidData = useMemo(() =>
    !hasError && pageData && translations.title && translations.subtitle1 && translations.subtitle2,
    [hasError, pageData, translations]
  );

  if (isLoading) {
    return <QuoteSkeleton />;
  }

  if (!hasValidData) {
    return <ErrorState onRetry={handleRetry} />;
  }

  return (
    <div className='home__quote__section'>
      <div className='quote__content'>
        <AnimatedOnScroll animation="fade-up" delay={0}>
          <h4 className='quote__title'>
            {translations.title}
          </h4>
        </AnimatedOnScroll>
        <AnimatedOnScroll animation="fade-up" delay={100}>
          <p className='quote__subtitle'>
            {translations.subtitle1}
          </p>
          <p className='quote__subtitle'>
            {translations.subtitle2}
          </p>
        </AnimatedOnScroll>

        <AnimatedOnScroll animation="fade-up" delay={100}>
          <div className='subscribe__quote'>
            <Formik
              key={formKey}
              initialValues={{ email: "" }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ values, handleSubmit, setFieldTouched, setFieldError }) => (
                <Form className="quote__subscribe__form" onSubmit={handleSubmit}>
                  <div className="quote__form__input__wrapper">
                    <Field
                      name="email"
                      type="email"
                      id="email"
                      placeholder=" "
                      autoComplete="email"
                      onInput={(e) => handleEmailInput(e, setFieldTouched, setFieldError)}
                    />
                    <label htmlFor="email">
                      {currentErrors.subscribeInputText}
                    </label>
                    {emailError && !values.email && (
                      <div className="quote__switch__keyboard__error">{emailError}</div>
                    )}
                    <ErrorMessage name="email" component="div" className="quote__error__message" />

                    {submitError && submitError === currentErrors.consentError && (
                      <div className="quote__error__message">
                        <p>{submitError}</p>
                      </div>
                    )}
                  </div>
                  <div className="quote__form__button__wrapper">
                    {formSubmitted ? (
                      <div className={`quote__form__success__message ${isFading ? "fade-out" : "fade-in"}`}>
                        <p>
                          {submitError
                            ? submitError
                            : currentErrors.subscribeSuccess}
                        </p>
                      </div>
                    ) : (
                      <button
                        type="submit"
                        className="quote__subscribe__button"
                        disabled={isSubmitting}
                      >
                        <span></span>
                        <span></span>
                        <span></span>
                        <span></span>
                        {isSubmitting ? currentErrors.submitting : currentErrors.subscribeButtonText}
                      </button>
                    )}
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </AnimatedOnScroll>
      </div>
    </div>
  );
};

export default React.memo(Quote);